import { Store } from '@ngrx/store';
import { searchViewportChanged } from '../../../search-history/search-history.actions';
import { SearchDomain, MapViewport, validViewport } from '../../../search-history/search-history.model';
import { MapListing } from '../../../models/map-listing.model';
import { MapListingCards } from '../../../services/map-listing-cards.service';
import { inject, NgZone, untracked } from '@angular/core';
import { ClusteredListingMarkers } from '../../maps/clustered-listing-markers';
import { OnChanges, SimpleChanges } from '@angular/core';
import { PolygonFilter } from '../../../models/polygon-filter.model';
import { PolygonDrawingAdapter } from '../../maps/polygon-drawing-adapter';
import { BreakpointObserver } from '@angular/cdk/layout';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  input,
  OnDestroy,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import * as L from 'leaflet';
import { Subject } from 'rxjs';
import { Car } from '../../../models/car-model';
import { MapParameters } from '../../../models/map-parameters.model';
import { RealEstate } from '../../../models/real-estate.model';
import { UserSearchResult } from '../../../models/user.model';

@Component({
  selector: 'app-map-result',
  standalone: false,
  templateUrl: './map-result.component.html',
  styleUrl: './map-result.component.scss',
})
export class MapResultComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() searchDomain?: SearchDomain;
  @Input() restoredViewport?: MapViewport;
  @Input() searchRestoreRevision = 0;
  private readonly historyStore = inject(Store, { optional: true });
  private rememberViewport(): void {
    if (!this.searchDomain || !this.resultMap) return;
    const center = this.resultMap.getCenter();
    const viewport = { latitude: center.lat, longitude: center.lng, zoom: this.resultMap.getZoom() };
    if (validViewport(viewport)) this.zone.run(() => this.historyStore?.dispatch(searchViewportChanged({ domain: this.searchDomain!, viewport })));
  }

  @ViewChild('mapContainer', { static: true })
  mapContainer!: ElementRef<HTMLDivElement>;

  @HostListener('window:resize')
  onResize() {
    this.zone.runOutsideAngular(() => this.resultMap?.invalidateSize({ pan: false }));
  }

  @Input() loading = false;
  @Input() polygonEnabled = false;
  @Input() polygonRevision = 0;
  readonly drawingReady = signal(false);
  private destroyed = false;
  @Input() activePolygon: PolygonFilter | null = null;
  @Input() searchError: string | null = null;
  readonly cardData = inject(MapListingCards, { optional: true });
  private readonly loadSelectedCard = effect(() => {
    const selected = this.currentGroupItem() ?? this.selectedItem();
    untracked(() => this.cardData?.select(selected?.id ?? null));
  });
  // Non-listing maps (e.g. agents) continue to use their existing complete result objects.
  cardFor(item: any): any {
    return !item || !this.cardData ? item : this.cardData.card(item.id);
  }

  @Output() polygonChanged = new EventEmitter<PolygonFilter | null>();
  readonly drawingMode = signal<'idle' | 'drawing' | 'editing'>('idle');
  readonly polygonError = signal<string | null>(null);
  private drawing?: PolygonDrawingAdapter;
  private drawingLoad?: Promise<void>;
  private timers = new Set<ReturnType<typeof setTimeout>>();

  @Input() icon = 'car';
  @Input() mapParameters?: MapParameters;

  private resultMap!: L.Map;
  private readonly zone = inject(NgZone);
  private readonly clustered = this.zone.runOutsideAngular(() => new ClusteredListingMarkers<any>((items, marker) => this.zone.run(() => {
    this.onPopupClose();
    if (items.length === 1) this.selectItem(items[0].id);
    else {
      this.activeGroupMarker = marker;
      marker.getElement()?.querySelector('.group-marker')?.classList.add('selected');
      this.showGroup(items);
    }
  })));
  private markersLayer = this.clustered.layer;
  private mapReady = signal(false);
  private initialViewFitted = false;
  private initialSizeObserver?: ResizeObserver;

  private markers = new Map<number, L.Marker>();
  private activeMarker: L.Marker | null = null;
  private activeGroupMarker: L.Marker | null = null;

  private defaultIcon!: L.Icon;
  private selectedIcon!: L.Icon;

  result = input<Car[] | RealEstate[] | UserSearchResult[] | MapListing[] | undefined>(undefined);
  selectedGroup = signal<any[]>([]);
  currentGroupIndex = signal(0);

  selectedItem = signal<any | null>(null);

  @Output() mapChanged = new EventEmitter<MapParameters>();

  private readonly syriaBounds = L.latLngBounds([32.3, 35.6], [37.3, 42.4]);

  /*
   * Navigation boundary only.
   * Gives visual context around Syria.
   */
  private readonly mapBounds = L.latLngBounds([29.0, 32.5], [40.0, 46.0]);

  private searchedAreaRectangle?: L.Rectangle;

  private destroy$ = new Subject<void>();

  constructor(private breakpointObserver: BreakpointObserver) {
    effect(() => {
      const data = this.result();
      const ready = this.mapReady();

      if (!ready) {
        return;
      }


      this.defaultIcon ??= L.icon({
        iconUrl: `icons/${this.icon}.png`,
        iconSize: [18, 18],
        iconAnchor: [10, 7],
      });

      this.selectedIcon ??= L.icon({
        iconUrl: `icons/${this.icon}2.png`,
        iconSize: [22, 22],
        iconAnchor: [12, 9],
      });

      // Selection changes must not become dependencies of this result-rendering effect.
      untracked(() => this.renderMarkers((data ?? []) as any[]));
    });
  }

  private updateBounds(): void {
    if (!this.resultMap) {
      return;
    }

    if (this.activePolygon || this.drawingMode() !== 'idle') {
      this.clearSearchedArea();
      return;
    }
    const bounds = this.getSearchBounds();

    if (!bounds) {
      this.clearSearchedArea();

      this.resultMap.fitBounds(this.syriaBounds, {
        padding: [20, 20],
      });

      return;
    }

    this.resultMap.fitBounds(bounds, {
      animate: false,
      padding: [0, 0],
    });

    /*
     * Highlight the area represented
     * by the current search results.
     */
    this.showSearchedArea(bounds);
  }

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => this.initializeMap());
  }

  private initializeMap(): void {
    this.initMap();
    this.initialSizeObserver = new ResizeObserver(() => this.fitInitialVisibleView());
    this.initialSizeObserver.observe(this.mapContainer.nativeElement);

    if (this.polygonEnabled) void this.ensureDrawing();
    this.mapReady.set(true);

    this.schedule(() => {
      this.resultMap.invalidateSize();

      this.fitInitialVisibleView();
    }, 0);
  }

  private getSearchBounds(): L.LatLngBounds | null {
    const parameters = this.mapParameters;

    if (
      !parameters ||
      !Number.isFinite(parameters.northEastLat) ||
      !Number.isFinite(parameters.northEastLng) ||
      !Number.isFinite(parameters.southWestLat) ||
      !Number.isFinite(parameters.southWestLng)
    ) {
      return null;
    }

    return L.latLngBounds(
      [parameters.southWestLat, parameters.southWestLng],
      [parameters.northEastLat, parameters.northEastLng]
    );
  }

  private showSearchedArea(bounds: L.LatLngBounds): void {
    if (!this.resultMap) {
      return;
    }

    this.searchedAreaRectangle?.remove();

    this.searchedAreaRectangle = L.rectangle(bounds, {
      color: '#dc3545',
      weight: 3,
      opacity: 1,

      fillColor: '#dc3545',
      fillOpacity: 0.08,

      interactive: false,
      pmIgnore: true,
      snapIgnore: true,
    }).addTo(this.resultMap);

    this.searchedAreaRectangle.bringToFront();
  }

  private clearSearchedArea(): void {
    this.searchedAreaRectangle?.remove();

    this.searchedAreaRectangle = undefined;
  }

  private initMap(): void {
    this.resultMap = L.map(this.mapContainer.nativeElement, {
      maxBounds: this.mapBounds,
      maxBoundsViscosity: 1,

      // MarkerCluster traverses integer zoom levels; fractional minima hide unclustered markers.
      minZoom: 5,
      maxZoom: 18,

      scrollWheelZoom: false,
      dragging: true,
    });

    this.resultMap.setMaxBounds(this.mapBounds);

    // A hidden Bootstrap tab has no usable bounds-fitting dimensions yet.
    this.resultMap.setView([34.8, 38.9], 7, { animate: false });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
    }).addTo(this.resultMap);

    this.markersLayer.addTo(this.resultMap);

    this.resultMap.on('moveend', () => this.rememberViewport());
    this.resultMap.on('click', () => this.zone.run(() => this.onPopupClose()));
  }

  private renderMarkers(items: any[]): void {
    if (this.clustered.updateData(items, this.defaultIcon)) {
      this.zone.run(() => {
        const latest = new Map(items.map(item => [item.id, item]));
        const selected = this.selectedItem();
        if (selected) this.selectedItem.set(latest.get(selected.id) ?? null);
        const group = this.selectedGroup();
        if (group.length) this.selectedGroup.set(group.map(item => latest.get(item.id) ?? item));
      });
      return;
    }
    this.zone.run(() => this.onPopupClose());
    this.markers = this.zone.runOutsideAngular(() => this.clustered.reconcile(items, this.defaultIcon));
  }

  searchArea(event: MouseEvent): void {
    if (this.activePolygon) this.polygonChanged.emit(this.activePolygon.map(p => ({...p})));
    else this.updateMap(event);
  }

  updateMap(event: MouseEvent): void {
    event.stopPropagation();
    if (this.activePolygon || this.drawingMode() !== 'idle') return;

    const bounds = this.resultMap.getBounds();

    this.mapChanged.emit({
      northEastLat: bounds.getNorthEast().lat,
      northEastLng: bounds.getNorthEast().lng,
      southWestLat: bounds.getSouthWest().lat,
      southWestLng: bounds.getSouthWest().lng,
    });
  }

  private fitInitialVisibleView(): void {
    if (this.destroyed || this.initialViewFitted || !this.resultMap || this.drawingMode() !== 'idle') return;
    const container = this.mapContainer.nativeElement;
    if (container.clientWidth <= 40 || container.clientHeight <= 40) return;
    this.resultMap.invalidateSize({ pan: false });
    if (validViewport(this.restoredViewport)) {
      this.resultMap.setView([this.restoredViewport.latitude, this.restoredViewport.longitude], this.restoredViewport.zoom, { animate: false });
    } else if (this.activePolygon) {
      this.resultMap.fitBounds(L.latLngBounds(this.activePolygon.map(p => L.latLng(p.latitude, p.longitude))),
        { padding: [30, 30], maxZoom: 15, animate: false });
    } else {
      this.updateBounds();
    }
    this.initialViewFitted = true;
    this.initialSizeObserver?.disconnect();
  }

  invalidateMap(): void {
    if (!this.resultMap) return;
    this.schedule(() => {
      this.resultMap.invalidateSize({ pan: false });
      this.fitInitialVisibleView();
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.zone.runOutsideAngular(() => this.updateInputs(changes));
  }

  private updateInputs(changes: SimpleChanges): void {
    if ((changes['activePolygon'] || changes['polygonRevision']) && this.drawing) {
      this.drawing.load(this.activePolygon, !!this.activePolygon);
      this.clearSearchedArea();
    }
    if (changes['mapParameters'] && this.resultMap) this.updateBounds();
    if (changes['searchRestoreRevision'] && this.resultMap) { this.initialViewFitted = false; this.fitInitialVisibleView(); }
    if (changes['searchError'] && this.searchError && this.activePolygon) this.drawing?.edit();
  }

  private ensureDrawing(): Promise<void> {
    if (this.drawing) return Promise.resolve();
    if (this.drawingLoad) return this.drawingLoad;
    this.drawingLoad = PolygonDrawingAdapter.create(this.resultMap, () =>
      this.zone.run(() => this.drawingMode.set(this.drawing?.mode ?? 'idle'))
    ).then(drawing => {
      if (this.destroyed) { drawing.destroy(); return; }
      this.drawing = drawing;
      drawing.load(this.activePolygon, !!this.activePolygon);
      this.drawingReady.set(true);
    }).catch(() => {
      if (!this.destroyed) this.polygonError.set('POLYGON_DRAW_INIT_FAILED');
    }).finally(() => { this.drawingLoad = undefined; });
    return this.drawingLoad;
  }

  async drawArea(): Promise<void> {
    this.polygonError.set(null);
    this.clearSearchedArea();
    this.onPopupClose();
    if (!this.drawing) await this.ensureDrawing();
    if (!this.destroyed && this.drawing) {
      try { this.drawing.draw(); }
      catch { this.polygonError.set('POLYGON_DRAW_INIT_FAILED'); }
    }
  }
  async editArea(): Promise<void> {
    this.polygonError.set(null);
    this.onPopupClose();
    if (!this.drawing) await this.ensureDrawing();
    if (!this.destroyed) this.drawing?.edit();
  }
  finishArea(): void {
    this.withPolygonError(() => this.drawing?.finish());
  }
  applyArea(): void {
    this.withPolygonError(() => {
      const polygon = this.drawing?.apply();
      if (polygon) this.polygonChanged.emit(polygon);
    });
  }
  cancelArea(): void {
    this.polygonError.set(null);
    this.drawing?.cancel();
    if (!this.activePolygon) this.updateBounds();
  }
  clearArea(): void {
    this.polygonError.set(null);
    this.drawing?.clear();
    this.clearSearchedArea();
    this.polygonChanged.emit(null);
  }
  private withPolygonError(action: () => void): void {
    try {
      this.polygonError.set(null);
      action();
    } catch (error) {
      this.polygonError.set(error instanceof Error ? error.message : 'POLYGON_INVALID_RING');
    }
  }
  private schedule(callback: () => void, delay = 0): void {
    const timer = this.zone.runOutsideAngular(() => setTimeout(() => {
      this.timers.delete(timer);
      callback();
    }, delay));
    this.timers.add(timer);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.initialSizeObserver?.disconnect();
    this.zone.runOutsideAngular(() => this.clustered.destroy());
    this.timers.forEach(clearTimeout);
    this.timers.clear();
    this.drawing?.destroy();
    this.searchedAreaRectangle?.remove();
    this.searchedAreaRectangle = undefined;

    if (this.resultMap) {
      this.resultMap.off();
      this.resultMap.remove();
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  onPopupClose(): void {
    this.selectedItem.set(null);
    this.selectedGroup.set([]);

    if (this.activeMarker) {
      this.activeMarker.setIcon(this.defaultIcon);
      this.activeMarker.setZIndexOffset(0);
      this.activeMarker = null;
    }

    if (this.activeGroupMarker) {
      this.activeGroupMarker
        .getElement()
        ?.querySelector('.group-marker')
        ?.classList.remove('selected');

      this.activeGroupMarker = null;
    }
  }

  private selectItem(id: number): void {
    // Clear selected group marker first.
    if (this.activeGroupMarker) {
      this.activeGroupMarker
        .getElement()
        ?.querySelector('.group-marker')
        ?.classList.remove('selected');

      this.activeGroupMarker = null;
    }

    // Clear group/card state.
    this.selectedGroup.set([]);
    this.currentGroupIndex.set(0);

    // Reset previous individual marker.
    if (this.activeMarker) {
      this.activeMarker.setIcon(this.defaultIcon);
      this.activeMarker.setZIndexOffset(0);

      this.activeMarker = null;
    }

    const marker = this.markers.get(id);

    if (!marker) {
      return;
    }

    // Highlight selected individual marker.
    marker.setIcon(this.selectedIcon);
    marker.setZIndexOffset(1000);

    this.activeMarker = marker;

    const selected = this.result()?.find((item) => item.id === id) ?? null;

    this.selectedItem.set(selected);
  }

  currentGroupItem = computed(() => {
    const group = this.selectedGroup();

    if (!group.length) {
      return null;
    }

    return group[this.currentGroupIndex()];
  });

  showGroup(items: any[]): void {
    this.selectedGroup.set(items);
    this.currentGroupIndex.set(0);
  }

  nextItem(): void {
    const group = this.selectedGroup();

    if (!group.length) return;

    this.currentGroupIndex.update((i) => (i + 1) % group.length);
  }

  previousItem(): void {
    const group = this.selectedGroup();

    if (!group.length) return;

    this.currentGroupIndex.update((i) => (i - 1 + group.length) % group.length);
  }
}
